import { useState } from "react";
import { Box, Button, Chip, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  DateField,
  FunctionField,
  Show,
  SimpleShowLayout,
  TextField,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";

import { adminApi } from "../../dataProvider";
import { API_URL } from "../../config";
import { sofStatus } from "./status";

type SofFile = { id: string; kind: string; kind_label: string; mime: string; size: number; url: string };
type HistoryItem = {
  id: string;
  status: string;
  comment: string | null;
  approved_amount_usd: number | null;
  reject_reason: string | null;
  files_count: number;
  submitted_at: string | null;
  is_current: boolean;
};
type SofRecord = {
  id: string;
  status: string;
  comment: string | null;
  approved_amount_usd: number | null;
  reject_reason: string | null;
  user_phone: string | null;
  user_email: string | null;
  user_addl_total_usd?: number;
  user_addl_used_usd?: number;
  files?: SofFile[];
  history?: HistoryItem[];
};

const fmtUsd = (n: number | null | undefined) =>
  n == null ? "—" : `$${n.toLocaleString("ru-RU", { maximumFractionDigits: 2 })}`;

function StatusChip() {
  const r = useRecordContext<SofRecord>();
  if (!r) return null;
  const s = sofStatus(r.status);
  return <Chip size="small" color={s.color} label={s.label} />;
}

function ActionButtons() {
  const record = useRecordContext<SofRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  if (!record) return null;
  const st = record.status;

  const call = async (action: "approve" | "reject", json?: unknown, okMsg?: string) => {
    setBusy(true);
    try {
      await adminApi(`/admin/sof/${record.id}/${action}`, { method: "POST", json });
      notify(okMsg ?? "Готово", { type: "success" });
      refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Ошибка", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const approve = () => {
    const raw = window.prompt("Сумма, на которую увеличить дополнительный лимит вывода клиента (USD):");
    if (raw === null) return;
    const amount = Number(raw.replace(",", ".").trim());
    if (!Number.isFinite(amount) || amount <= 0) {
      notify("Введите положительную сумму в USD", { type: "error" });
      return;
    }
    // без приложенных документов (клиент подал их брокеру/банку напрямую) — комментарий обязателен
    let comment: string | undefined;
    if (!record.files?.length) {
      const c = window.prompt("Документов нет — укажите обязательный комментарий (напр. «документы предоставлены в банк»):");
      if (c === null) return;
      if (!c.trim()) {
        notify("Комментарий обязателен при одобрении без документов", { type: "error" });
        return;
      }
      comment = c.trim();
    }
    call("approve", { amount, comment }, `ИПДС одобрена (+${fmtUsd(amount)})`);
  };

  const reject = () => {
    const reason = window.prompt("Причина отклонения (увидит клиент):");
    if (reason === null) return;
    call("reject", { reason }, "ИПДС отклонена");
  };

  if (st === "approved") return <Chip color="success" icon={<CheckCircleIcon />} label={`Одобрена (+${fmtUsd(record.approved_amount_usd)})`} />;

  return (
    <Stack direction="row" spacing={2}>
      <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} disabled={busy} onClick={approve}>
        Одобрить
      </Button>
      <Button variant="outlined" color="error" startIcon={<CancelIcon />} disabled={busy} onClick={reject}>
        Отклонить
      </Button>
    </Stack>
  );
}

function LimitContext() {
  const r = useRecordContext<SofRecord>();
  if (!r) return null;
  const total = r.user_addl_total_usd ?? 0;
  const used = r.user_addl_used_usd ?? 0;
  return (
    <Typography variant="body2" color="text.secondary">
      Доп. лимит клиента сейчас: {fmtUsd(total)} всего · {fmtUsd(used)} использовано · остаток {fmtUsd(Math.max(0, total - used))}
    </Typography>
  );
}

function HistoryList() {
  const r = useRecordContext<SofRecord>();
  const items = r?.history ?? [];
  if (items.length === 0) return <Typography variant="body2">Прошлых заявок нет</Typography>;
  return (
    <Stack spacing={1}>
      {items.map((h) => {
        const s = sofStatus(h.status);
        return (
          <Paper key={h.id} variant="outlined" sx={{ p: 1.5, borderColor: h.is_current ? "primary.main" : undefined }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Chip size="small" color={s.color} label={s.label} />
              {h.is_current && <Chip size="small" variant="outlined" label="текущая" />}
              <Typography variant="caption" color="text.secondary">
                {h.submitted_at ? new Date(h.submitted_at).toLocaleString("ru-RU") : ""}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                · {h.files_count} док.
              </Typography>
            </Stack>
            {h.status === "approved" && <Typography variant="body2" color="success.main">+{fmtUsd(h.approved_amount_usd)} к лимиту</Typography>}
            {h.status === "rejected" && h.reject_reason && (
              <Typography variant="body2" color="error">
                Причина: {h.reject_reason}
              </Typography>
            )}
            {h.comment && (
              <Typography variant="body2" color="text.secondary">
                Комментарий: {h.comment}
              </Typography>
            )}
          </Paper>
        );
      })}
    </Stack>
  );
}

function FilesGrid() {
  const record = useRecordContext<SofRecord>();
  if (!record?.files?.length) return <Typography variant="body2">Документов нет</Typography>;
  return (
    <Grid container spacing={2}>
      {record.files.map((f) => (
        <Grid item xs={12} md={4} key={f.id}>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {f.kind_label}
            </Typography>
            {f.mime === "application/pdf" ? (
              <Button component="a" href={API_URL + f.url} target="_blank" rel="noreferrer" variant="outlined" fullWidth>
                Открыть PDF
              </Button>
            ) : (
              <Box component="a" href={API_URL + f.url} target="_blank" rel="noreferrer" sx={{ display: "block" }}>
                <Box
                  component="img"
                  src={API_URL + f.url}
                  alt={f.kind}
                  sx={{ display: "block", width: "100%", maxHeight: 320, objectFit: "contain", bgcolor: "#000", borderRadius: 1 }}
                />
              </Box>
            )}
            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
              {f.mime} · {Math.round(f.size / 1024)} KB
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

export function SofShow() {
  return (
    <Show>
      <SimpleShowLayout>
        <FunctionField label="Статус" render={() => <StatusChip />} />
        <FunctionField label="Действие" render={() => <ActionButtons />} />
        <FunctionField label="Лимит клиента" render={() => <LimitContext />} />
        <FunctionField
          label="Комментарий клиента"
          render={(r: SofRecord) => (r.comment ? <Typography variant="body2">{r.comment}</Typography> : null)}
        />
        <Divider />
        <TextField source="user_phone" label="Телефон" />
        <TextField source="user_email" label="Email" />
        <DateField source="submitted_at" label="Отправлена" showTime />
        <FunctionField label="Документы (НДФЛ, справка о доходах)" render={() => <FilesGrid />} />
        <Divider />
        <FunctionField label="История заявок клиента" render={() => <HistoryList />} />
      </SimpleShowLayout>
    </Show>
  );
}
